/**
 * WatchtowerPage
 *
 * Main Deadlines page with list view, filtering, and entry creation.
 * Follows the layout pattern from CareLogsPage (sidebar + main content).
 */

import { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { useCaseFile } from '../contexts/CaseFileContext';
import { Sidebar, Header } from '../components/dashboard';
import {
  DeadlineForm,
  DeadlinesList,
  DeadlineDetail,
  DeadlineFilters,
  DeadlineFormData,
  DeadlineStatus,
  DeadlineType,
  Deadline,
} from '../components/deadlines';
import { useDeadlineCalendarExport } from '../hooks/useDeadlineCalendarExport';
import { Plus, ArrowLeft, X, Filter, Eye } from 'lucide-react';

interface CreateDeadlineResponse {
  success: boolean;
  deadline?: unknown;
  error?: string;
}

export function WatchtowerPage(): JSX.Element {
  const { caseFileId: urlCaseFileId } = useParams<{ caseFileId: string }>();
  const navigate = useNavigate();

  // Use CaseFile context
  const {
    caseFiles,
    activeCaseFileId,
    setActiveCaseFileId,
    refreshCaseFiles,
  } = useCaseFile();

  // Sync URL caseFileId with context
  const caseFileId = urlCaseFileId || activeCaseFileId;

  // Update context if URL has a different caseFileId
  useEffect(() => {
    if (urlCaseFileId && urlCaseFileId !== activeCaseFileId) {
      setActiveCaseFileId(urlCaseFileId);
    }
  }, [urlCaseFileId, activeCaseFileId, setActiveCaseFileId]);

  // UI state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showNewDeadlineForm, setShowNewDeadlineForm] = useState(false);
  const [selectedDeadlineId, setSelectedDeadlineId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showFilters, setShowFilters] = useState(false);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<DeadlineStatus[]>([]);
  const [typeFilter, setTypeFilter] = useState<DeadlineType[]>([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const hasFilters = statusFilter.length > 0 || typeFilter.length > 0 || startDate || endDate;
  const filterCount = statusFilter.length + typeFilter.length + (startDate || endDate ? 1 : 0);

  // Calendar export wiring (TASK-016)
  const { open: openExportModal, Modal: CalendarExportModal } = useDeadlineCalendarExport();

  // `recipientName` is intentionally `undefined` (not `''`) when the active
  // CaseFile's name has not yet resolved. The modal interprets undefined as
  // "name not available" and force-disables the PHI toggle + forces the
  // audit payload's `includedCategories.recipientName` to false. Treating
  // unresolved as empty-string would silently allow the toggle to be on
  // while the generator skips the name — producing a false audit trail.
  // (TASK-016 Codex remediation, finding 1.)
  const recipientName = caseFiles.find(
    (cf) => cf.caseFileId === caseFileId,
  )?.name;

  const handleExport = useCallback(
    (deadline: Deadline) => {
      openExportModal(deadline, { recipientName });
    },
    [openExportModal, recipientName],
  );

  // Card-triggered export. The card only has a `DeadlineSummary`, so we pass
  // a `DeadlineRef` and let the modal fetch the full document itself. Any
  // network/auth/App Check failure surfaces through the modal's existing
  // inline error banner with a Retry button — no silent failures, no toast
  // pattern, single source of error UX. (TASK-016 Codex remediation, finding 2.)
  const handleExportFromCard = useCallback(
    (deadlineId: string) => {
      if (!caseFileId) return;
      openExportModal(
        { caseFileId, deadlineId },
        { recipientName },
      );
    },
    [caseFileId, openExportModal, recipientName],
  );

  const handleCreateDeadline = useCallback(async (data: DeadlineFormData) => {
    if (!caseFileId) return;

    setIsCreating(true);

    try {
      const createDeadline = httpsCallable<
        {
          caseFileId: string;
          title: string;
          dueDate: string;
          type: string;
          priority: string;
          description?: string;
          relatedDocumentId?: string;
          alerts?: Array<{ daysBeforeDeadline: number; channels: string[]; enabled: boolean }>;
          isRecurring?: boolean;
          recurrencePattern?: string;
        },
        CreateDeadlineResponse
      >(functions, 'createDeadline');

      const result = await createDeadline({
        caseFileId,
        title: data.title,
        dueDate: data.dueDate,
        type: data.type,
        priority: data.priority,
        description: data.description,
        relatedDocumentId: data.relatedDocumentId,
        alerts: data.alerts,
        isRecurring: data.isRecurring,
        recurrencePattern: data.recurrencePattern,
      });

      if (result.data.success) {
        setShowNewDeadlineForm(false);
        setRefreshKey((prev) => prev + 1);
      } else {
        throw new Error(result.data.error || 'Failed to create deadline');
      }
    } catch (err) {
      console.error('Error creating deadline:', err);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, [caseFileId]);

  const handleDeadlineClick = (deadlineId: string) => {
    setSelectedDeadlineId(deadlineId);
  };

  const handleEditClick = (deadlineId: string) => {
    setSelectedDeadlineId(deadlineId);
  };

  const handleBackFromDetail = () => {
    setSelectedDeadlineId(null);
  };

  const handleDeadlineUpdated = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const clearAllFilters = () => {
    setStatusFilter([]);
    setTypeFilter([]);
    setStartDate('');
    setEndDate('');
  };

  const handleBackToCaseFile = () => {
    navigate(`/casefile/${caseFileId}`);
  };

  const handleCaseFileChange = (newCaseFileId: string) => {
    setActiveCaseFileId(newCaseFileId);
    navigate(`/casefile/${newCaseFileId}/deadlines`);
  };

  const handleJoinedCareTeam = (newCaseFileId: string) => {
    setActiveCaseFileId(newCaseFileId);
    refreshCaseFiles();
    navigate(`/casefile/${newCaseFileId}/deadlines`);
  };

  if (!caseFileId) {
    return (
      <div className="min-h-screen bg-airy flex items-center justify-center">
        <div className="card p-8 max-w-md text-center">
          <h2 className="text-lg font-semibold text-ink mb-2">No Care Record Selected</h2>
          <p className="text-muted mb-6">Please select a care record from the dashboard to view deadlines.</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-airy flex">
      {/* Sidebar - Desktop */}
      <div className="hidden lg:block">
        <Sidebar className="h-screen sticky top-0" activeCaseFileId={caseFileId} />
      </div>

      {/* Sidebar - Mobile Overlay */}
      {sidebarOpen && (
        <>
          <div
            className="fixed inset-0 bg-ink/40 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 lg:hidden">
            <Sidebar className="h-full" activeCaseFileId={caseFileId} />
          </div>
        </>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Global Header with CaseFile Selector */}
        <Header
          caseFiles={caseFiles.map((cf) => ({ id: cf.caseFileId, name: cf.name }))}
          activeCaseFileId={caseFileId}
          onCaseFileChange={handleCaseFileChange}
          onMenuClick={() => setSidebarOpen(true)}
          showMobileMenu={true}
          onJoinedCareTeam={handleJoinedCareTeam}
        />

        {/* Page Header */}
        <div className="bg-white border-b border-soft-neutral px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={handleBackToCaseFile}
                className="btn-ghost btn-sm p-1 lg:hidden"
                aria-label="Back to CaseFile"
              >
                <ArrowLeft size={20} aria-hidden="true" />
              </button>
              <button
                onClick={handleBackToCaseFile}
                className="hidden lg:flex btn-ghost btn-sm items-center gap-1"
              >
                <ArrowLeft size={16} />
                Back to CaseFile
              </button>
              <div className="h-6 w-px bg-soft-neutral hidden lg:block" />
              <div className="flex items-center gap-2">
                <Eye size={20} className="text-evergreen" />
                <h1 className="text-xl font-semibold text-ink">Watchtower</h1>
              </div>
            </div>
            <button
              onClick={() => setShowNewDeadlineForm(true)}
              className="btn-primary flex items-center gap-2"
            >
              <Plus size={18} />
              <span className="hidden sm:inline">Add Deadline</span>
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div id="main-content" role="main" tabIndex={-1} className="flex-1 p-4 lg:p-6 outline-none">
          <div className="max-w-3xl mx-auto">
            {/* New Deadline Form */}
            {showNewDeadlineForm && (
              <div className="mb-6">
                <div className="card">
                  <div className="card-header flex justify-between items-center">
                    <h3 className="font-semibold text-ink">New Deadline</h3>
                    <button
                      onClick={() => setShowNewDeadlineForm(false)}
                      className="btn-ghost btn-sm p-1"
                    >
                      <X size={18} />
                    </button>
                  </div>
                  <div className="card-body">
                    <DeadlineForm
                      onSubmit={handleCreateDeadline}
                      onCancel={() => setShowNewDeadlineForm(false)}
                      submitLabel="Create Deadline"
                      isLoading={isCreating}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Detail View (when a deadline is selected) */}
            {selectedDeadlineId && (
              <div className="mb-6">
                <DeadlineDetail
                  caseFileId={caseFileId}
                  deadlineId={selectedDeadlineId}
                  onBack={handleBackFromDetail}
                  onUpdate={handleDeadlineUpdated}
                  onExport={handleExport}
                />
              </div>
            )}

            {/* List View (when no deadline is selected) */}
            {!selectedDeadlineId && (
              <>
                {/* Filters Toggle */}
                <div className="mb-4 flex items-center justify-between">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`btn-ghost btn-sm flex items-center gap-2 ${hasFilters ? 'text-evergreen' : ''}`}
                  >
                    <Filter size={16} />
                    {showFilters ? 'Hide Filters' : 'Show Filters'}
                    {hasFilters && (
                      <span className="badge badge-primary badge-sm">{filterCount}</span>
                    )}
                  </button>
                  {hasFilters && (
                    <button
                      onClick={clearAllFilters}
                      className="btn-ghost btn-sm text-muted hover:text-ink"
                    >
                      Clear All Filters
                    </button>
                  )}
                </div>

                {/* Filters Panel */}
                {showFilters && (
                  <div className="card mb-6">
                    <div className="card-body">
                      <DeadlineFilters
                        statusFilter={statusFilter}
                        typeFilter={typeFilter}
                        startDate={startDate}
                        endDate={endDate}
                        onStatusChange={setStatusFilter}
                        onTypeChange={setTypeFilter}
                        onStartDateChange={setStartDate}
                        onEndDateChange={setEndDate}
                        onClearAll={clearAllFilters}
                      />
                    </div>
                  </div>
                )}

                {/* Deadlines List */}
                <DeadlinesList
                  caseFileId={caseFileId}
                  statusFilter={statusFilter.length > 0 ? statusFilter : undefined}
                  typeFilter={typeFilter.length > 0 ? typeFilter : undefined}
                  startDate={startDate || undefined}
                  endDate={endDate || undefined}
                  onDeadlineClick={handleDeadlineClick}
                  onEditClick={handleEditClick}
                  onExportClick={handleExportFromCard}
                  refreshKey={refreshKey}
                />
              </>
            )}
          </div>
        </div>
      </div>

      {/* Calendar export modal (TASK-016) — bound to internal hook state */}
      <CalendarExportModal />
    </div>
  );
}

export default WatchtowerPage;
